// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import composeScheduledSubTitle from 'app/common/react/components/ScheduledPin/SchedulingPinSubtitle';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import SectionHeader from 'app/common/react/components/profile/SectionHeader/SectionHeader';
import type { ScheduledPin } from 'app/common/react/components/ScheduledPin/types';
import type { State as ReduxState } from 'app/common/redux/types';
import { compose } from 'redux';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { track } from 'app/common/lib/react/analytics';
import withResource, { type Resource } from 'app/common/lib/react/withResource';
import { Box, Container, Icon, Image, Text } from 'gestalt';

const PIN_CELL_LENGTH = 126;
const MAX_PIN_NUM = 6;

type Props = {|
  username: string,
  pinResource: Resource<Array<ScheduledPin>>,
  pins: Array<ScheduledPin>,
|};

export class UserProfileScheduledPinOverview extends Component<Props> {
  seeAllScheduledPinHandler = () => {
    track(PCONST.thrift.EventType.CLICK, {
      element: PCONST.thrift.ElementType.SCHEDULED_PIN_OVERVIEW_SEE_ALL_BUTTON,
    });
  };

  renderScheduledPin = (pin: ScheduledPin, plusMore: number) => {
    // TODO: (tianli) implement a better function like i18n.getHumanizedTimesince
    const timeFromNow = Math.floor(
      (pin.scheduled_ts - new Date().getTime() / 1000) / (60 * 60 * 24),
    );
    const timeFromNowStr = interpolateNamedTemplate(
      i18n._(
        '{{ timeFromNow }}d',
        '"d" here stands for "days", the whole string means how many days e.g. 1 day, 2 days',
      ),
      { timeFromNow },
    );
    const plusMoreStr = `+ ${plusMore}`;
    const overlay =
      plusMore > 0 ? (
        <React.Fragment>
          <Box
            height="100%"
            width="100%"
            position="absolute"
            top
            bottom
            left
            right
            dangerouslySetInlineStyle={{
              __style: {
                backgroundColor: '#000',
                opacity: 0.5,
              },
            }}
          />
          <Box
            height="100%"
            width="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            position="absolute"
            top
            bottom
            left
            right
          >
            <Text color="white" size="sm" weight="bold">
              {plusMoreStr}
            </Text>
          </Box>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <Box
            height="100%"
            width="100%"
            position="absolute"
            top
            bottom
            left
            right
            dangerouslySetInlineStyle={{
              __style: {
                backgroundColor: '#000',
                opacity: 0.1,
              },
            }}
          />
          <Box
            alignItems="center"
            bottom
            display="flex"
            justifyContent="start"
            left
            paddingX={3}
            paddingY={2}
            position="absolute"
            width="100%"
          >
            <Icon
              accessibilityLabel={i18n._('scheduled time', 'Time scheduled for publishing the pin')}
              color="white"
              icon="clock"
              size={12}
            />
            <Box display="inlineBlock" marginLeft={1}>
              <Text color="white" size="sm">
                {timeFromNowStr}
              </Text>
            </Box>
          </Box>
        </React.Fragment>
      );

    const { image: images, title = '' } = pin;
    const image = images && images['236x'];

    return (
      <React.Fragment>
        {image && (
          <Image
            alt={title}
            color="lightGray"
            fit="cover"
            naturalHeight={image.height}
            naturalWidth={image.width}
            src={image.url}
          />
        )}
        {overlay}
      </React.Fragment>
    );
  };

  renderEmtpyPlaceholder = () => <Box color="lightGray" height="100%" width="100%" />;

  render() {
    const {
      username,
      pinResource: { isLoaded },
      pins,
    } = this.props;

    if (!isLoaded || !pins || pins.length === 0) {
      return null;
    }

    const pinCount = pins.length;

    return (
      <Box marginBottom={10}>
        <Container>
          <SectionHeader
            label={i18n._(
              'Scheduled Pins',
              'Title for the scheduled Pin section under the pin tab of profile page',
            )}
            sublabel={composeScheduledSubTitle(pinCount)}
            link={`/${username}/scheduled-pins/`}
            onSeeAllButtonClick={this.seeAllScheduledPinHandler}
          />
          <NavigateLink to={`/${username}/scheduled-pins/`}>
            <Box
              data-test-id="scheduled-pins-nav-link"
              height={PIN_CELL_LENGTH}
              marginLeft={3}
              marginRight={3}
              overflow="hidden"
              shape="rounded"
            >
              {Array(MAX_PIN_NUM)
                .fill()
                .map((_, index) => (
                  <Box
                    key={index}
                    display="inlineBlock"
                    height={PIN_CELL_LENGTH}
                    marginRight={index === MAX_PIN_NUM - 1 ? 0 : 1}
                    overflow="hidden"
                    position="relative"
                    width={PIN_CELL_LENGTH}
                  >
                    {index < pinCount
                      ? this.renderScheduledPin(
                          pins[index],
                          index === 5 ? pinCount - MAX_PIN_NUM : 0,
                        )
                      : this.renderEmtpyPlaceholder()}
                  </Box>
                ))}
            </Box>
          </NavigateLink>
        </Container>
      </Box>
    );
  }
}

export default compose(
  withResource({
    name: 'ScheduledPinsResource',
    key: 'pinResource',
  }),
  connect((state: ReduxState) => ({
    pins: state.scheduledPins,
  })),
)(UserProfileScheduledPinOverview);
